import { useAuth0 } from '@auth0/auth0-react'
import { useQuery } from '@tanstack/react-query'
import { useConfigStore } from '@stores'

interface GrantedAuthScopesState {
  grantedScopes: string[]
  isLoading: boolean
}

/**
 * The scopes Auth0 actually granted to this session. Permissions are
 * requested as scopes (see `auth0Scopes`); RBAC only grants the ones the
 * user holds, so the granted scopes of the token response reveal the user's
 * permissions -- no token parsing needed. Shared by the permission hooks
 * (`useHasAdminPermission`, `useHasAiPermission`, `useIsDevUser`) via one
 * cached query.
 */
const useGrantedAuthScopes = (): GrantedAuthScopesState => {
  const { isAuthenticated, getAccessTokenSilently } = useAuth0()
  const auth0Audience = useConfigStore(state => state.auth0Audience)
  const auth0Scopes = useConfigStore(state => state.auth0Scopes)

  const { data, isLoading } = useQuery({
    queryKey: ['grantedAuthScopes'],
    queryFn: async () => {
      const { scope } = await getAccessTokenSilently({
        detailedResponse: true,
        authorizationParams: {
          audience: auth0Audience,
          scope: auth0Scopes,
        },
      })

      return (scope ?? '').split(' ')
    },
    enabled: isAuthenticated,
    staleTime: Infinity,
  })

  return {
    grantedScopes: data ?? [],
    isLoading: isAuthenticated && isLoading,
  }
}

export default useGrantedAuthScopes
